import { create } from 'zustand';
import { CategoryInfo, PriceType, ProductTileResponse, SearchSortByEnum, SupplierAggregation, SupplierDeliveryWindow, VirtualScrollResponseDTO } from '@api/generated/search-service';
import { CatalogReponse } from '@api/generated/pecosta-search-service';
import { catalogApi, clientAutocompleteControllerApi } from '@api/api-config.ts';
import { useOrgPortal } from '@hooks/useOrgPortal.ts';
import { useFavouritesStore } from '@features/favouries/useFavouritesStore.ts';
import { applyProductTileUpdateToList, mergeSupplierDelivery, subscribeProductTileUpdate } from '@features/product-detail/productTileUpdates.ts';

// Mapování UI hodnot na API enum hodnoty
const sortMap: Record<string, SearchSortByEnum> = {
  'relevance': SearchSortByEnum.Relevance,
  'name-asc': SearchSortByEnum.SortAz,
  'name-desc': SearchSortByEnum.SortZa,
  'price-asc': SearchSortByEnum.SortPriceAsc,
  'price-desc': SearchSortByEnum.SortPriceDesc,
};

const priceTypeMap: Record<string, PriceType> = {
  standard: PriceType.Standard,
  alternative: PriceType.SubstitutePrice,
  sales: PriceType.ActionPrice,
  delivery: PriceType.FromDlPrice,
};

export interface CatalogFilters {
  sort?: string;
  priceFilter?: string;
  isGuaranteed?: boolean;
}

interface CatalogInfiniteScrollState {
  // Query state
  itemsPerRow: number;
  initialRowCount: number;
  categoryEcid: string;
  supplierIds: number[];
  supplierFilterLevel: number | undefined;
  filters: CatalogFilters;
  isInitialized: boolean;

  // Data state
  results: (ProductTileResponse | null)[]; // Pole s produkty nebo null (mockup)
  supplierDelivery: SupplierDeliveryWindow[] | null;
  supplierAggregations: SupplierAggregation[];
  totalCount: number;
  hasMore: boolean;
  nextStartIndex: number;
  categoryInfo: CategoryInfo | null;

  // UI state
  loading: boolean;
  error: string | null;
  loadingRows: Set<number>; // Sledování načítaných řádků

  // Actions
  fetchMore: () => Promise<void>;
  fetchMoreForRow: (triggeredRowIndex: number) => Promise<void>;
  initializeCatalog: (categoryEcid: string | null, supplierIds: number[] | undefined, supplierFilterLevel: number | undefined, filters?: CatalogFilters) => Promise<void>;
  clearResults: () => void;
  loadCategoryInfo: () => Promise<void>;
  isRowLoading: (rowIndex: number) => boolean;
}

const initialRowCount = 5; // Větší initial load pro virtuální scroll

export const useCatalogInfiniteScroll = create<CatalogInfiniteScrollState>((set, get) => ({
  // Query state
  itemsPerRow: 8,
  initialRowCount: initialRowCount,
  isInitialized: false,
  filters: {},

  // Data state
  results: [],
  supplierDelivery: [],
  supplierAggregations: [],
  totalCount: 0,
  hasMore: true,
  nextStartIndex: 0,
  categoryInfo: null,
  supplierFilterLevel: undefined,
  supplierIds: [],
  categoryEcid: '',

  // UI state
  loading: false,
  error: null,
  loadingRows: new Set<number>(),

  // Actions
  clearResults: () => {
    set({
      results: [],
      totalCount: 0,
      hasMore: true,
      nextStartIndex: 0,
      categoryInfo: null,
      error: null,
    });
  },

  loadCategoryInfo: async () => {
    const { orgId: organizationId, portalId } = useOrgPortal.getState();
    const { categoryEcid, supplierIds } = get();

    if (!categoryEcid) {
      console.log('❌ CategoryEcid is empty, cannot load category info');
      return;
    }

    try {
      const response = await catalogApi.getCatalog({
        portalId: portalId,
        buyerId: organizationId,
        categoryEcid: categoryEcid,
        firstNProducts: 1, // We only need category info, not products
        supplierIds: supplierIds ?? [],
      });

      const catalogData: CatalogReponse = response.data;

      // Convert CatalogReponse to CategoryInfo format
      // Data z catalog-service mají pole volitelná (number | undefined), search-service
      // typy je vyžadují — na hranici je proto sjednotíme defaultem.
      const categoryInfo: CategoryInfo = {
        categoryEcid: catalogData.categoryEcid,
        categoryId: catalogData.categoryId ?? 0,
        categoryName: catalogData.categoryName,
        categoryImageUrl: undefined,
        subcategories: catalogData.categories.map(cat => ({
          categoryEcid: cat.categoryEcid,
          categoryId: cat.categoryId ?? 0,
          categoryName: cat.categoryName,
          categoryImageUrl: cat.categoryImage ?? undefined,
          productCount: cat.totalProductsCount ?? 0,
        })),
        breadcrumb: catalogData.breadcrumb.map(bc => ({
          categoryEcid: bc.categoryEcid,
          categoryId: bc.categoryId ?? 0,
          categoryName: bc.categoryName,
          categoryImageUrl: undefined,
        })),
      };

      set({ categoryInfo });
    } catch (error) {
      console.error('❌ Error loading category info:', error);
      set({ categoryInfo: null });
    }
  },

  initializeCatalog: async (categoryEcid, supplierIds, supplierFilterLevel, filters = {}) => {
    const { initialRowCount, itemsPerRow } = get();
    const { orgId: organizationId, portalId } = useOrgPortal.getState();

    if (!categoryEcid) {
      console.log('❌ CategoryEcid is empty, returning');
      return;
    }

    try {
      // při přechodu na jinou kategorii vyčistit starý obsah, aby neproblikl, než dorazí nová data
      const isCategoryChange = get().categoryEcid !== categoryEcid;
      set({
        loading: true,
        error: null,
        categoryEcid,
        supplierIds,
        supplierFilterLevel,
        filters,
        isInitialized: true,
        ...(isCategoryChange
          ? { results: [], totalCount: 0, hasMore: true, nextStartIndex: 0, categoryInfo: null }
          : {}),
      });

      console.log('initializeCatalog', { categoryEcid, organizationId, portalId, supplierIds, supplierFilterLevel, filters });

      useFavouritesStore.getState().loadFavourites();

      const sortBy = filters.sort ? sortMap[filters.sort] : sortMap['relevance'];
      const filterPriceType = filters.priceFilter ? priceTypeMap[filters.priceFilter] : undefined;

      // Load category info and products in parallel
      const [, productsPromise] = await Promise.allSettled([
        get().loadCategoryInfo(),
        clientAutocompleteControllerApi.search({
          query: '',
          categoryEcid,
          organizationId,
          portalId,
          startIndex: 0,
          endIndex: initialRowCount * itemsPerRow,
          supplierIds: supplierIds ?? [],
          sortBy,
          filterGarantedPrice: filters.isGuaranteed,
          filterPriceType,
        }),
      ]);

      // Handle products response
      if (productsPromise.status === 'fulfilled') {
        const data: VirtualScrollResponseDTO = productsPromise.value.data;
        const products = data.products || [];

        const totalCount = data.totalCount || 0;

        // Vytvoříme pole o velikosti totalCount s produkty na začátku a null na konci
        const resultsArray: (ProductTileResponse | null)[] = new Array(totalCount).fill(null);
        products.forEach((product, index) => {
          resultsArray[index] = product;
        });

        set({
          loading: false,
          results: resultsArray,
          totalCount: totalCount,
          hasMore: products.length < totalCount,
          nextStartIndex: initialRowCount * itemsPerRow,
          error: null,
          supplierDelivery: data.supplierDelivery,
          supplierAggregations: data.supplierAggregations || [],
        });
      } else {
        console.error('❌ Error loading products:', productsPromise.reason);
        set({
          loading: false,
          error: 'Chyba při načítání produktů',
        });
      }
    } catch (error) {
      console.error('❌ Error in initializeCatalog:', error);
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Neznámá chyba při načítání katalogu',
      });
    }
  },

  fetchMoreForRow: async (_triggeredRowIndex: number) => {
    const { results, itemsPerRow, loading, categoryEcid, supplierIds, filters } = get();
    const { orgId: organizationId, portalId } = useOrgPortal.getState();

    const loadedProductsCount = results.filter(item => item !== null).length;
    const hasMoreProducts = loadedProductsCount < results.length;

    if (!hasMoreProducts || loading || !categoryEcid) {
      return;
    }

    // Najdi první index s null hodnotou (první nenačtený produkt)
    const firstNullIndex = results.findIndex(item => item === null);
    if (firstNullIndex === -1) {
      return;
    }

    try {
      set({ loading: true, error: null });

      // Načteme 5 řádků najednou od prvního nenačteného produktu
      const startIndex = firstNullIndex;
      const endIndex = Math.min(startIndex + itemsPerRow * 5, results.length);

      const sortBy = filters.sort ? sortMap[filters.sort] : sortMap['relevance'];
      const filterPriceType = filters.priceFilter ? priceTypeMap[filters.priceFilter] : undefined;

      const response = await clientAutocompleteControllerApi.search({
        query: '',
        categoryEcid,
        organizationId,
        portalId,
        startIndex,
        endIndex,
        supplierIds: supplierIds ?? [],
        sortBy,
        filterGarantedPrice: filters.isGuaranteed,
        filterPriceType,
      });

      const data: VirtualScrollResponseDTO = response.data;
      const newProducts = data.products || [];

      const totalCount = data.totalCount || 0;

      // Nahradíme null hodnoty skutečnými produkty na správných pozicích
      const newResults = [...results];

      newProducts.forEach((product, index) => {
        const position = startIndex + index;
        if (position < totalCount) {
          newResults[position] = product;
        }
      });

      // Spočítáme kolik produktů je načteno
      const loadedProductsCount = newResults.filter(item => item !== null).length;

      set({
        loading: false,
        results: newResults,
        hasMore: loadedProductsCount < totalCount,
        nextStartIndex: endIndex,
        error: null,
        supplierDelivery: data.supplierDelivery,
      });
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Neznámá chyba při načítání dalších produktů',
      });
    }
  },

  fetchMore: async () => {
    const { results, nextStartIndex, itemsPerRow, hasMore, loading, categoryEcid, supplierIds, filters } = get();
    const { orgId: organizationId, portalId } = useOrgPortal.getState();

    if (!hasMore || loading || !categoryEcid) {
      return;
    }

    try {
      set({ loading: true, error: null });

      const sortBy = filters.sort ? sortMap[filters.sort] : sortMap['relevance'];
      const filterPriceType = filters.priceFilter ? priceTypeMap[filters.priceFilter] : undefined;

      const response = await clientAutocompleteControllerApi.search({
        query: '',
        categoryEcid,
        organizationId,
        portalId,
        startIndex: nextStartIndex,
        endIndex: nextStartIndex + itemsPerRow,
        supplierIds: supplierIds ?? [],
        sortBy,
        filterGarantedPrice: filters.isGuaranteed,
        filterPriceType,
      });

      const data: VirtualScrollResponseDTO = response.data;
      const newProducts = data.products || [];

      const totalCount = data.totalCount || 0;

      // Nahradíme null hodnoty skutečnými produkty na správných pozicích
      const newResults = [...results];
      newProducts.forEach((product, index) => {
        const position = nextStartIndex + index;
        if (position < totalCount) {
          newResults[position] = product;
        }
      });

      // Spočítáme kolik produktů je načteno
      const loadedProductsCount = newResults.filter(item => item !== null).length;

      set({
        loading: false,
        results: newResults,
        hasMore: loadedProductsCount < totalCount,
        nextStartIndex: nextStartIndex + itemsPerRow,
        error: null,
        supplierDelivery: data.supplierDelivery,
      });
    } catch (error) {
      console.error('❌ Error in fetchMore:', error);
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Neznámá chyba při načítání dalších produktů',
      });
    }
  },

  isRowLoading: (rowIndex: number) => {
    const { loadingRows } = get();
    return loadingRows.has(rowIndex);
  },
}));

subscribeProductTileUpdate(update =>
  useCatalogInfiniteScroll.setState(state => ({
    results: applyProductTileUpdateToList(state.results, update),
    supplierDelivery: mergeSupplierDelivery(state.supplierDelivery, update),
  })),
);
